import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { Logger } from '../../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../../infrastructure/context/user-context-provider';
import { PolicyPrice, PolicyPriceColumns } from '../../../policies/models/Policy';
import { PriceRatesService } from '../../services/price-rates.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-property-manager-table',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIcon,
    MatTabsModule,
    MatTableModule,
    MatFormFieldModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    FormsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './property-manager-table.component.html',
  styleUrl: './property-manager-table.component.scss'
})
export class PropertyManagerTableComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort = new MatSort;

  loading = new BehaviorSubject<boolean>(false)
  propertyManagerDataSource = new MatTableDataSource<PolicyPrice>();
  propertyManagerDisplayedColumns = PolicyPriceColumns;
  propertyManagerTableLength = 0;
  propertyMangagerPageSize = 10;
  propertyManagerPriceRates: PolicyPrice[] = [];
  displayedPropertyMangerColumnNames: string[] = [];
  propertyManagerSearchKeyword: string = '';
  pageNumber: number = 1;

  initialKeyword = new BehaviorSubject<string>('');
  initialSort = new BehaviorSubject<Sort | undefined>(undefined);
  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);

  constructor(
    private router: Router,
    private priceRatesService: PriceRatesService,
    public userContextProvider: UserContextProvider,
    private logService: Logger,
    private matSnackbar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.initTableColumns();
    this.loadPage();

    const storedItemsPerPage = localStorage.getItem('propertyManagerPriceRatesItemsPerPage');
    if (storedItemsPerPage) {
      this.initialPageSize.next(parseInt(storedItemsPerPage, 10));
    } else {
      this.initialPageSize.next(10);
    }
  }

  ngAfterViewInit() {
    this.propertyManagerDataSource.paginator = this.paginator;
    this.propertyManagerDataSource.sort = this.sort;

    this.initialPageSize?.subscribe(pageSize => {
      this.propertyMangagerPageSize = pageSize;
      this.paginator.pageSize = pageSize;
      this.cdr.detectChanges();
    });

    this.initialPageNumber?.subscribe(pageNumber => {
      if (pageNumber != 1) {
        this.pageNumber = pageNumber;
        this.paginator.pageIndex = pageNumber;
      } else {
        this.pageNumber = 1;
      }
      this.cdr.detectChanges();
    });

    this.initialKeyword?.subscribe(keyword => {
      this.propertyManagerSearchKeyword = keyword;
      this.cdr.detectChanges();
    });

    this.initialSort?.subscribe(sort => {
      if (!sort) {
        return;
      }

      this.setSort(sort);
      this.cdr.detectChanges();
    });
  }

  setSort(sort: Sort) {
    this.sort.sort({ id: sort.active, start: sort.direction, disableClear: false });
  }

  initTableColumns(): void {
    if (this.propertyManagerDisplayedColumns === null) {
      this.propertyManagerDisplayedColumns = [];
    }
    this.displayedPropertyMangerColumnNames = this.propertyManagerDisplayedColumns.map(c => c.column);
  }

  async loadPage() {
    try {
      this.loading.next(true);
      this.propertyManagerDataSource.data = [];

      const priceRates = await this.priceRatesService.getPriceRates();
      const mappedPriceRates = priceRates.map(p => this.buildPriceRateTableRow(p));

      this.propertyManagerPriceRates = mappedPriceRates.filter(priceRate => priceRate.type_id === 2);

      this.updatePropertyManagerTableDataSource(this.propertyManagerPriceRates);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Price Rates', 'Error while retrieving Price Rates');
    } finally {
      this.loading.next(false);
    }
  }

  buildPriceRateTableRow(priceRate: PolicyPrice): any {
    const priceModel = this.priceRatesService.getPriceModelById(priceRate.pricing_model_id);

    return {
      id: priceRate.id,
      name: priceRate.name,
      pricing_model: priceModel,
      currency: priceRate.currency.code,
      price: priceRate.price ? priceRate.price : 0,
      deductible: priceRate.deductible ? priceRate.deductible : 0,
      type_id: priceRate.type_id,
      night_ranges: priceRate.night_ranges,
      extra_night_price: priceRate.extra_night_price
    }
  }

  updatePropertyManagerTableDataSource(priceRates: PolicyPrice[]) {
    this.propertyManagerDataSource.data = priceRates;
    this.propertyManagerTableLength = priceRates.length;
  }

    onPropertyManagerPageEvent(event: PageEvent): void {
    this.propertyManagerDataSource = new MatTableDataSource(this.propertyManagerPriceRates);
    this.propertyMangagerPageSize = event.pageSize;
    const end = (event.pageIndex + 1) * this.propertyMangagerPageSize;
    const start = event.pageIndex * this.propertyMangagerPageSize;
    this.propertyManagerDataSource = new MatTableDataSource(this.propertyManagerDataSource.data.slice(start, end));
  }

  onPropertyManagerSearchEvent(keyword: string): void {
    this.propertyManagerDataSource.filter = keyword.trim().toLowerCase();
  }

  applyPropertyManagerSearch(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.propertyManagerDataSource.filter = filterValue.trim().toLowerCase();

    if (this.propertyManagerDataSource.paginator) {
      this.propertyManagerDataSource.paginator.firstPage();
    }
  }

  clearPropertyManagerSearch() {
    this.propertyManagerSearchKeyword = '';
    this.propertyManagerDataSource = new MatTableDataSource(this.propertyManagerPriceRates);
    this.ngAfterViewInit();
  }

  onGoToPropertyMangerPageClick() {
    const max = Math.trunc(Math.ceil(this.paginator.length / this.paginator.pageSize));
    if (this.pageNumber < 1) {
      this.resetPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.pageNumber > max) {
      this.resetPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      this.paginator.pageIndex = this.pageNumber - 1;
      this.paginator.page.next({
        pageIndex: this.pageNumber - 1,
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      });
    }
  }

  onPageEvent(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    localStorage.setItem('propertyManagerPriceRatesItemsPerPage', event.pageSize.toString());
  }

  resetPaginator(pageNumber: number, goToFirstPage: boolean) {
    this.pageNumber = pageNumber;

    if (this.paginator) {
      if (goToFirstPage) {
        this.paginator.firstPage();
      } else {
        this.paginator.lastPage();
      }
    }
  }

  displayInvalidPaginatorPageIndexError() {
    this.matSnackbar.open('Invalid page number', 'OK', { duration: 3000 });
  }

  onRowClick(row: any): void {
    this.router.navigate([`/price-rates/detail/${row['id']}`]);
  }
}